import { Documentation_PageMetadataKey } from '@tyro/api';
import { assembleBlocks, parseBlocks } from './block-parser';
import type { PageMetadataRow } from './page-metadata';

interface BlockAccess {
  userPermissions?: Set<string>;
  userType?: string;
}

/**
 * Filters block-gated article markdown down to the blocks the user may see.
 *
 * A block (identified by its `<!-- block:id -->` marker) can be tagged via
 * page metadata with a required permission set and/or allowed user types:
 * - permission-set tags keep the block only if the user holds one of them;
 * - user-type tags keep the block only if the user's type is listed.
 * Untagged blocks always pass. The visible blocks are reassembled back into
 * markdown. Content with no block metadata is returned unchanged.
 */
export function gateMarkdownByAccess(
  contentMd: string,
  metadata: PageMetadataRow[] | undefined,
  access: BlockAccess,
): string {
  if (!contentMd) return '';
  if (!metadata || metadata.length === 0) return contentMd;

  const blockMeta = metadata.filter((row) => row.blockId);
  if (blockMeta.length === 0) return contentMd;

  const { userPermissions, userType } = access;
  const blocks = parseBlocks(contentMd);

  const visibleBlocks = blocks.filter((block) => {
    const requiredPermissions = blockMeta
      .filter(
        (row) =>
          row.blockId === block.id &&
          row.key === Documentation_PageMetadataKey.PermissionSet,
      )
      .map((row) => row.value);

    if (
      requiredPermissions.length > 0 &&
      userPermissions &&
      !requiredPermissions.some((permission) => userPermissions.has(permission))
    ) {
      return false;
    }

    const allowedUserTypes = blockMeta
      .filter(
        (row) =>
          row.blockId === block.id &&
          row.key === Documentation_PageMetadataKey.UserType,
      )
      .map((row) => row.value);

    if (
      allowedUserTypes.length > 0 &&
      userType &&
      !allowedUserTypes.includes(userType)
    ) {
      return false;
    }

    return true;
  });

  return assembleBlocks(visibleBlocks);
}
